import React from 'react';
import { SignedImage } from '../components/SignedImage';
import { motion } from 'framer-motion';
import { Info, GitBranch, History, ChevronRight, RefreshCw } from 'lucide-react';

export const Version: React.FC = () => {
  const [versionData, setVersionData] = React.useState({ 
    major: 0, 
    minor: 0, 
    patch: 0, 
    lastUpdate: new Date().toISOString() 
  });
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const fetchVersion = async () => {
    try {
      setIsRefreshing(true);
      const res = await fetch('/api/version');
      if (res.ok) {
        const data = await res.json();
        setVersionData(data);
      }
    } catch (e) {
      console.error('Failed to fetch version:', e);
    } finally {
      setIsRefreshing(false);
    }
  };

  React.useEffect(() => {
    fetchVersion();
  }, []);

  const versionString = `${versionData.major}.${versionData.minor}.${versionData.patch}`;
  const lastUpdate = new Date(versionData.lastUpdate).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const [statusMessage, setStatusMessage] = React.useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingConfirm, setPendingConfirm] = React.useState<'patch' | 'minor' | 'major' | null>(null);

  // Function to wait for server to be back online
  const waitForRestart = async () => {
    let attempts = 0;
    const maxAttempts = 15;
    
    while (attempts < maxAttempts) {
      try {
        const res = await fetch('/api/health');
        if (res.ok) {
          return true;
        }
      } catch (e) {
      }
      attempts++;
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    return false;
  };

  const incrementVersion = async (type: 'patch' | 'minor' | 'major') => {
    setIsUpdating(true);
    setStatusMessage({ type: 'info', text: `Aggiornamento versione ${type.toUpperCase()} in corso...` });
    setPendingConfirm(null);

    try {
      const response = await fetch('/api/version/increment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      
      const data = await response.json();
      if (data.success) {
        setStatusMessage({ 
          type: 'success', 
          text: `Database aggiornato a v${data.newVersion}.` 
        });
        
        // Give the server 1.5s to initiate the restart if needed (though DB sync is direct)
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setStatusMessage({ 
          type: 'info', 
          text: `Sincronizzazione ambiente in corso... Attendere.` 
        });

        // Wait for the server to be healthy again
        const reconnected = await waitForRestart();

        if (reconnected) {
          await fetchVersion(); // Refresh local state
          setStatusMessage({ 
            type: 'success', 
            text: `Sincronizzazione completata! Versione aggiornata a v${data.newVersion}.` 
          });
          
          // Small delay before potential reload just to be sure, or just stay here
          setTimeout(() => {
            // If they still want to reload to be 100% sure:
            // window.location.reload();
            setStatusMessage(null);
          }, 3000);
        } else {
          setStatusMessage({ 
            type: 'error', 
            text: `Il server sta impiegando troppo tempo a riavviarsi. Prova a ricaricare manualmente tra qualche secondo.` 
          });
        }
      } else {
        let errorMsg = `Errore dal server: ${data.error}`;
        if (data.debug) {
          console.error('Debug Version Error:', data.debug);
          errorMsg += '. (Verifica log console browser per dettagli tecnici)';
        }
        setStatusMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Error incrementing version:', error);
      setStatusMessage({ type: 'error', text: 'Errore di connessione al server.' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">
      {statusMessage && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-2xl border flex items-center justify-between ${
            statusMessage.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700' :
            statusMessage.type === 'error' ? 'bg-red-500/10 border-red-500/20 text-red-700' :
            'bg-blue-500/10 border-blue-500/20 text-blue-700'
          }`}
        >
          <div className="flex items-center space-x-3">
            <Info className="w-5 h-5" />
            <p className="font-medium">{statusMessage.text}</p>
          </div>
          <button 
            onClick={() => setStatusMessage(null)}
            className="text-xs font-bold uppercase tracking-widest hover:opacity-70"
          >
            Chiudi
          </button>
        </motion.div>
      )}

      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-6 ${isUpdating ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
            <SignedImage 
              path="images/icons/version-control.webp" 
              bucket="cpgev"
              alt="Version Control"
              className="w-10 h-10 object-contain"
            />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Versione Sistema</h1>
            <p className="text-muted-foreground mt-1">Dettagli sulla versione corrente e cronologia aggiornamenti</p>
          </div>
        </div>
        <div className="bg-card border rounded-2xl px-6 py-3 shadow-sm flex items-center space-x-3">
          <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Corrente:</span>
          <span className="text-2xl font-mono font-bold text-primary">v{versionString}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border rounded-2xl p-6 shadow-sm flex flex-col space-y-3"
        >
          <div className="p-2 bg-amber-500/10 text-amber-600 rounded-lg w-fit">
            <Info className="w-5 h-5" />
          </div>
          <h3 className="font-bold text-lg">MAJOR</h3>
          <p className="text-sm text-muted-foreground">Incrementato per cambiamenti alle API o su richiesta specifica.</p>
          <div className="pt-2 flex items-center justify-between">
            <div className="text-2xl font-mono font-bold">{versionData.major}</div>
            <button 
              onClick={() => setPendingConfirm('major')}
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-amber-500/10 text-amber-600 rounded hover:bg-amber-500/20 transition-colors"
            >
              Incrementa
            </button>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border rounded-2xl p-6 shadow-sm flex flex-col space-y-3"
        >
          <div className="p-2 bg-blue-500/10 text-blue-600 rounded-lg w-fit">
            <GitBranch className="w-5 h-5" />
          </div>
          <h3 className="font-bold text-lg">MINOR</h3>
          <p className="text-sm text-muted-foreground">Incrementato ogni volta che viene effettuato un push su Github.</p>
          <div className="pt-2 flex items-center justify-between">
            <div className="text-2xl font-mono font-bold">{versionData.minor}</div>
            <button 
              onClick={() => setPendingConfirm('minor')}
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-blue-500/10 text-blue-600 rounded hover:bg-blue-500/20 transition-colors"
            >
              Incrementa
            </button>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border rounded-2xl p-6 shadow-sm flex flex-col space-y-3"
        >
          <div className="p-2 bg-emerald-500/10 text-emerald-600 rounded-lg w-fit">
            <History className="w-5 h-5" />
          </div>
          <h3 className="font-bold text-lg">PATCH</h3>
          <p className="text-sm text-muted-foreground">Incrementato automaticamente ad ogni salvataggio file.</p>
          <div className="pt-2 text-2xl font-mono font-bold">{versionData.patch}</div>
        </motion.div>
      </div>

      <div className="bg-card border rounded-2xl overflow-hidden shadow-sm">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="font-bold text-xl">Dettagli Deploy</h3>
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            import.meta.env.PROD 
              ? 'bg-emerald-500/10 text-emerald-600' 
              : 'bg-amber-500/10 text-amber-600'
          }`}>
            {import.meta.env.PROD ? 'Produzione' : 'Sviluppo'}
          </span>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Ultimo aggiornamento:</span>
            <span className="font-medium">{lastUpdate}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Ambiente:</span>
            <span className="font-medium">Cloud Run / Vite + React</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Database:</span>
            <span className="font-medium text-primary">Supabase (PostgreSQL)</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-4">
        <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-widest pl-2">Azioni Versioning</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col space-y-2">
            <button 
              className={`flex items-center justify-between p-4 transition-colors rounded-2xl border group text-left ${
                pendingConfirm === 'major' 
                  ? 'bg-amber-500/20 border-amber-500/40' 
                  : 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20'
              }`}
              onClick={() => setPendingConfirm(pendingConfirm === 'major' ? null : 'major')}
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center text-amber-600">
                  <Info className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-amber-700 text-sm">Incrementa MAJOR</p>
                  <p className="text-[10px] text-amber-600/70">Cambi API o modifiche strutturali</p>
                </div>
              </div>
              <ChevronRight className={`w-5 h-5 text-amber-600 transition-transform ${pendingConfirm === 'major' ? 'rotate-90' : 'group-hover:translate-x-1'}`} />
            </button>
            
            {pendingConfirm === 'major' && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-4 flex flex-col space-y-3"
              >
                <p className="text-xs text-amber-800 font-medium">Sicuro? Questo azzererà Minor e Patch. L'azione è irreversibile.</p>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => incrementVersion('major')}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition-colors"
                  >
                    Sì, incrementa Major
                  </button>
                  <button 
                    onClick={() => setPendingConfirm(null)}
                    className="px-4 py-2 bg-amber-500/10 text-amber-700 rounded-lg text-xs font-bold hover:bg-amber-500/20 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              </motion.div>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <button 
              className={`flex items-center justify-between p-4 transition-colors rounded-2xl border group text-left ${
                pendingConfirm === 'minor' 
                  ? 'bg-blue-500/20 border-blue-500/40' 
                  : 'bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20'
              }`}
              onClick={() => setPendingConfirm(pendingConfirm === 'minor' ? null : 'minor')}
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-600">
                  <GitBranch className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-blue-700 text-sm">Incrementa MINOR (Sync)</p>
                  <p className="text-[10px] text-blue-600/70">Prepara la versione per il Push su GitHub</p>
                </div>
              </div>
              <ChevronRight className={`w-5 h-5 text-blue-600 transition-transform ${pendingConfirm === 'minor' ? 'rotate-90' : 'group-hover:translate-x-1'}`} />
            </button>

            {pendingConfirm === 'minor' && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-4 flex flex-col space-y-3"
              >
                <p className="text-xs text-blue-800 font-medium">Incrementare per congruenza dev-produzione? Patch verrà azzerato.</p>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => incrementVersion('minor')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg text-xs font-bold hover:bg-blue-600 transition-colors"
                  >
                    Sì, incrementa Minor
                  </button>
                  <button 
                    onClick={() => setPendingConfirm(null)}
                    className="px-4 py-2 bg-blue-500/10 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-500/20 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        <button 
          className="flex items-center justify-between w-full p-4 bg-accent/30 hover:bg-accent/50 transition-colors rounded-2xl border border-transparent hover:border-border group text-left"
          onClick={() => window.location.reload()}
        >
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-background rounded-xl flex items-center justify-center text-primary">
              <History className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold">Aggiorna l'App</p>
              <p className="text-xs text-muted-foreground">Svuota cache locale e ricarica i componenti</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      <p className="text-center text-[10px] text-muted-foreground/50 pb-8">
        GESVOL PLATFORM v{versionString} &copy; 2026 GEV Bologna. Tutti i diritti riservati.
      </p>
    </div>
  );
};
